import { Metrics } from "../_app/cuchillo/core/Metrics";
import { Sizes } from "../_app/cuchillo/core/Sizes";
import { Maths } from "../_app/cuchillo/utils/Maths";
import { isSmartphone } from "../_app/cuchillo/core/Basics";

export default class Line {
    static container;
    static ctx;

    static progress = 1;
    static linearProgress = 1;
    static speed = 0.006;

    static color = '#FF5E36';
    static width = 2 * Sizes.RATIO;

    static points = [];
    static oldPoints = [];
    static sampledPoints = [];
    static oldSampledPoints = [];

    static offset = { x: 0, y: 0 };

    static easingFn = () => { };
    static easings = [
        function easeInOutQuad(t) {
            return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        }
        ,
        function easeInOutCubic(t) {
            return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        }
        ,
        function easeInOutSine(t) {
            return -(Math.cos(Math.PI * t) - 1) / 2;
        }
    ];

    static init(ctx, container = document.body) {
        this.ctx = ctx;
        this.container = container;

        const center = { x: Metrics.WIDTH / 2 * Sizes.RATIO, y: Metrics.HEIGHT / 2 * Sizes.RATIO };
        this.points = [center];
        this.sampledPoints = [center];

        this.easingFn = this.easings[0];
    }

    static goto(point) {
        this.offset.x = point.x * Sizes.RATIO;
        this.offset.y = point.y * Sizes.RATIO;
    }

    static directLineTo(point) {
        const p = { x: point.x * Sizes.RATIO, y: point.y * Sizes.RATIO }
        this.points = [p];
        this.sampledPoints = [p];
    }

    static lineTo(point, isDirect = false) {
        // Last point
        const pLast = { x: point.x * Sizes.RATIO, y: point.y * Sizes.RATIO };

        // No movement
        if (pLast.x === this.points[this.points.length - 1].x && pLast.y === this.points[this.points.length - 1].y) return;

        // Calculating beginning and end points;
        // First point
        const pStart = this.points[this.points.length - 1];
        const points = [pStart];

        // Reduce curve intensity on mobile — offsets are absolute px,
        // which are proportionally huge on a 375px-wide canvas
        const curveMult = isSmartphone ? 0.45 : 1;

        // Point after first
        const OFFSET_Y = (80 * Sizes.RATIO + Math.random() * 20 * Sizes.RATIO) * curveMult;
        const OFFSET_X = (Math.random() - .5) * 50 * Sizes.RATIO * curveMult;
        const p0 = { x: pStart.x + OFFSET_X, y: pStart.y + OFFSET_Y };
        points.push(p0);

        // Point before last
        const ARRIVE_OFFSET_Y = (60 * Sizes.RATIO + Math.random() * 80 * Sizes.RATIO) * curveMult;
        const ARRIVE_OFFSET_X = (40 * Sizes.RATIO + Math.random() * 50 * Sizes.RATIO) * curveMult;
        const dir = pLast.x > pStart.x ? -1 : 1;
        const p1 = {
            x: pLast.x + dir * ARRIVE_OFFSET_X,
            y: pLast.y + ARRIVE_OFFSET_Y
        };

        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const numExtraPoints = Math.floor(dist / 500);
        const baseSpeed = 0.018;
        const refDist = 250;
        const exponent = 0.35;
        this.speed = Math.min(0.01, Math.max(0.006, baseSpeed * Math.pow(dist / refDist, exponent)));

        // punts intermedis
        for (let i = 0; i < numExtraPoints; i++) {
            const VAR_X = (Math.random() - 0.5) * 200 * Sizes.RATIO * curveMult;
            const VAR_Y = ((Math.random() - 0.5) < 0 ? -1 : 1) * (80 * Sizes.RATIO + Math.random() * 70 * Sizes.RATIO) * curveMult;

            const t = (i + 1) / (numExtraPoints + 1);
            const x = Maths.lerp(p0.x, p1.x, t) + VAR_X;
            const y = Maths.lerp(p0.y, p1.y, t) + VAR_Y;

            points.push({ x, y });
        }

        points.push(p1);
        points.push(pLast);

        if (!isDirect) {
            // Ressetting progress
            this.progress = 0;
            this.linearProgress = 0;
            this.easingFn = this.easings[Math.floor(Math.random() * this.easings.length)];

            // Swapping new for old points
            this.oldPoints = this.points.slice();
            this.oldSampledPoints = this.sampledPoints.slice();
        }


        this.points = points;
        this.sampledPoints = this.sampleCatmullRom(points);
    }

    static pointToPoint(p1, p2) {
        const pFirst = { x: p1.x * Sizes.RATIO, y: p1.y * Sizes.RATIO };

        if (pFirst.x === this.points[0].x && pFirst.y === this.points[0].y) return;

        this.points = [pFirst];
        this.sampledPoints = [pFirst];
        this.oldPoints = [];
        this.oldSampledPoints = [];
        this.lineTo(p2, true);
    }

    static isInsideContainer(x, y) {
        return x > 0 && x < this.container.clientWidth && y > 0 && y < this.container.clientHeight;
    }

    static update() {
        if (this.progress >= 1) return;
        this.linearProgress = Math.min(1, this.linearProgress + this.speed);
        this.progress = this.easingFn(this.linearProgress);
    }

    static draw() {
        const { ctx, color, width, progress, sampledPoints, oldSampledPoints } = this;

        ctx.clearRect(0, 0, this.container.width, this.container.height);

        const ox = this.offset.x;
        const oy = this.offset.y;

        ctx.strokeStyle = color;
        ctx.lineWidth = width;

        // CURVES
        if (sampledPoints && sampledPoints.length > 1) {
            const total = sampledPoints.length;
            const visible = Math.floor(total * progress);

            ctx.beginPath();
            ctx.moveTo(sampledPoints[0].x + ox, sampledPoints[0].y + oy);

            for (let i = 1; i < visible; i++) {
                ctx.lineTo(sampledPoints[i].x + ox, sampledPoints[i].y + oy);
            }
            ctx.stroke();
        }

        if (oldSampledPoints && oldSampledPoints.length > 1) {
            const total = oldSampledPoints.length;
            const visible = Math.floor(total * (1 - progress));

            ctx.beginPath();
            ctx.moveTo(oldSampledPoints[total - 1].x + ox, oldSampledPoints[total - 1].y + oy);

            for (let i = total - 2; i > total - visible - 2; i--) {
                if (i < 0) break;
                ctx.lineTo(oldSampledPoints[i].x + ox, oldSampledPoints[i].y + oy);
            }

            ctx.stroke();
        }
    }

    static loop() {
        this.update();
        this.draw();
        this.drawTriangle();
    }

    static sampleCatmullRom(points, samplesPerSegment = 20) {
        if (points.length < 2) return points;

        const sampled = [];

        for (let i = 0; i < points.length - 1; i++) {
            const p0 = points[Math.max(i - 1, 0)];
            const p1 = points[i];
            const p2 = points[i + 1];
            const p3 = points[Math.min(i + 2, points.length - 1)];

            for (let j = 0; j < samplesPerSegment; j++) {
                const t = j / samplesPerSegment;
                const tt = t * t;
                const ttt = tt * t;

                const x = 0.5 * ((2 * p1.x) +
                    (-p0.x + p2.x) * t +
                    (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * tt +
                    (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * ttt);

                const y = 0.5 * ((2 * p1.y) +
                    (-p0.y + p2.y) * t +
                    (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * tt +
                    (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * ttt);

                sampled.push({ x, y });
            }
        }

        sampled.push(points[points.length - 1]);
        return sampled;
    }

    static drawTriangle() {
        if (!this.sampledPoints || this.sampledPoints.length < 2) return;

        const total = this.sampledPoints.length;
        const index = Math.min(total - 1, Math.floor(total * this.progress));
        const last = this.sampledPoints[index];

        const prevIndex = Math.max(0, index - 5);
        const prev = this.sampledPoints[prevIndex];

        const ox = this.offset.x;
        const oy = this.offset.y;
        const size = 12 * Sizes.RATIO_CANVAS;

        const ctx = this.ctx;
        ctx.fillStyle = this.color;

        const angle = Math.atan2(last.y - prev.y, last.x - prev.x);

        ctx.save();
        ctx.translate(last.x + ox, last.y + oy);
        ctx.rotate(angle + Math.PI / 2);

        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(-size / 2, size);
        ctx.lineTo(size / 2, size);
        ctx.closePath();
        ctx.fill();

        ctx.restore();
    }
}